import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { useNewHomeService } from '../hooks/useNewHomeService';
import { newHomeBuild } from '../../config/newHome.build';
import NewHomeSectionRenderer from '../components/NewHomeSectionRenderer';
import FloatingNavigation from '../components/FloatingNavigation';
import HomeSeoContentSection from '@/features/home/presentation/components/HomeSeoContentSection';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { trackUserContextLoaded } from '../analytics/homePageUserContextLoaded';
import { getPosCartCount } from '@/features/posCart/utils/posCartUtils';
import { usePosCart } from '@/features/posCart/presentation/hooks/usePosCart';
import FloatingStoreVisitCta from '@/features/posCart/presentation/components/FloatingStoreVisitCta';
import {
  setMobileHeaderConfig,
  resetMobileHeaderConfig,
  setDesktopHeaderConfig,
  resetDesktopHeaderConfig,
} from '@/app/store/headerConfigSlice';
import FloatingNavigationUnified from '../components/FloatingNavigationUnified';

import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';

export default function NewHomePage() {
  const dispatch = useDispatch<AppDispatch>();
  const { homePageVariant: pageVariant } = useMultivariateExperiment();
  const { service } = useMemo(() => newHomeBuild(dispatch), [dispatch]);
  const {
    sections,
    floatingNav,
    mobileHeader,
    desktopHeader,
    homeSeoContentSection,
    isLoading,
    getRecommendedProducts,
    fetchBrowseFilterPreview,
  } = useNewHomeService({ service });
  const { analytics, isInitialized } = useAnalytics();
  const hasTrackedViewRef = useRef(false);
  const hasTrackedUserContextRef = useRef(false);

  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    if (!analytics || !isInitialized) return;

    try {
      analytics.trackEvent('Home Page Viewed');
      hasTrackedViewRef.current = true;
    } catch (error) {
      console.error('Error tracking New Home Page Viewed:', error);
    }
  }, [analytics, isInitialized]);

  const { userContext, userCode, customerId } = useUserContext();
  const { items, fetchItems, fetchNextPage, hasMore } = usePosCart();

  const activeUserCode = userCode ?? customerId;

  useEffect(() => {
    if (hasTrackedUserContextRef.current) return;
    const isHomePageVariant = pageVariant === 'V4' || pageVariant === 'V5';
    if (!analytics || !isInitialized || !activeUserCode || !isHomePageVariant) return;
    hasTrackedUserContextRef.current = true;
    console.log('Client-side User Context Loaded event triggered!', isInitialized, activeUserCode, isHomePageVariant);
    const loadAndTrackContext = async () => {
      try {
        const res = await fetchItems({
          customerCode: String(activeUserCode),
          cartItemsOnly: false,
          includeWishlist: pageVariant === 'V4',
          includeWebCart: pageVariant === 'V5',
          page: 1,
          size: 10,
        });
        trackUserContextLoaded(analytics, res ?? []);
      } catch (err) {
        console.error('Failed to load homepage POS cart items:', err);
        trackUserContextLoaded(analytics, []);
      }
    };
    void loadAndTrackContext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, isInitialized, activeUserCode, pageVariant]);

  const hasActiveStoreCart = useMemo(() => getPosCartCount(items) >= 1, [items]);

  const showStoreSelections = Boolean(
    userContext?.isLoggedIn &&
    hasActiveStoreCart &&
    (pageVariant === 'V4' || pageVariant === 'V5')
  );

  useEffect(() => {
    if (mobileHeader) {
      dispatch(setMobileHeaderConfig(mobileHeader));
    } else {
      dispatch(resetMobileHeaderConfig());
    }
    if (desktopHeader) {
      dispatch(setDesktopHeaderConfig(desktopHeader));
    } else {
      dispatch(resetDesktopHeaderConfig());
    }
    return () => {
      dispatch(resetMobileHeaderConfig());
      dispatch(resetDesktopHeaderConfig());
    };
  }, [dispatch, mobileHeader, desktopHeader]);

  useEffect(() => {
    document.body.classList.add('nh-page');
    return () => {
      document.body.classList.remove('nh-page');
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <NewHomeSectionRenderer
        sections={sections}
        getRecommendedProducts={getRecommendedProducts}
        fetchBrowseFilterPreview={fetchBrowseFilterPreview}
        posCartItems={items}
        showStoreSelections={showStoreSelections}
        onLoadMoreStoreSelections={fetchNextPage}
        hasMoreStoreSelections={hasMore}
      />
      {homeSeoContentSection ? (
        <HomeSeoContentSection
          data={homeSeoContentSection}
          sectionKey="homeSeoContentSection"
          firstColumnTitleAsH1
        />
      ) : null}
      {floatingNav?.enabled && pageVariant !== 'V6' && pageVariant !== 'V7' && pageVariant !== 'V8' && <FloatingNavigation config={floatingNav} />}
      {floatingNav?.enabled && (pageVariant === 'V6' || pageVariant === 'V7' || pageVariant === 'V8') && <FloatingNavigationUnified config={floatingNav} />}
      {showStoreSelections && <FloatingStoreVisitCta />}
    </div>
  );
}
